import React, { useState, useEffect } from 'react';
import { Button, Card, Box, TextField, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Snackbar, Dialog, DialogContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Alert from '@mui/material/Alert';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CircularProgress from '@mui/material/CircularProgress';
import CollectionsIcon from '@mui/icons-material/Collections';
import CancelIcon from '@mui/icons-material/Cancel';



const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: 'white',
        color: 'black',
        fontSize: 15,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

function Distributor() {
    const [formData, setFormData] = useState({
        Distri_ID: '',
        full_name: '',
        Email_Address: '',
        Phone_Number: '',
        address: '',
    });

    const [files, setFiles] = useState({
        nicFront: null,
        nicBack: null
    });


    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [errors, setErrors] = useState({});
    const [editDistributorId, setEditDistributorId] = useState(null);
    const [distributors, setDistributors] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [nicFrontImage, setNicFrontImage] = useState('');
    const [nicBackImage, setNicBackImage] = useState('');
    const [imageDialog, setImageDialog] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteDistId, setDeleteDistId] = useState();

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Handle Phone_Number input to ensure only digits and a max of 10 characters
        if (name === 'Phone_Number') {
            const phoneNumberPattern = /^[0-9]*$/; // Only allow numbers
            if (phoneNumberPattern.test(value) && value.length <= 10) {
                setFormData((prevState) => ({
                    ...prevState,
                    [name]: value,
                }));
            }
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [name]: value,
            }));
        }
    };

    const handleConfirmDelete = async () => {
        try {
            await axios.delete(`https://servermunidasa.ccsoftwares.com/distributors/deleteDistributor/${deleteDistId}`);
            fetchDistributors();
            setMessage(' Distributor removed successfully!');
            setSeverity('success');
            setOpen(true);
        } catch (error) {
            console.error('Error distributor dealer:', error);
            setMessage('Failed to delete distributor. Please try again.');
            setSeverity('error');
            setOpen(true);
        } finally {
            setDeleteDialogOpen(false);
            setDeleteDistId(null);
        }
    };

    const handleFileChange = (e) => {

        const { name, files } = e.target;
        setFiles(prevFiles => ({
            ...prevFiles,
            [name]: files[0]
        }));
    };

    const validate = () => {
        let tempErrors = {};
        tempErrors.Distri_ID = formData.Distri_ID ? '' : 'Distributor ID is required.';
        tempErrors.full_name = formData.full_name ? '' : 'Name is required.';
        tempErrors.Email_Address = formData.Email_Address ? '' : 'Email address is required.';
        tempErrors.Phone_Number =
            formData.Phone_Number.length === 10 ? '' : 'Phone number must be exactly 10 digits.';
        tempErrors.address = formData.address ? '' : 'Address is required.';
        setErrors(tempErrors);
        return Object.values(tempErrors).every((x) => x === '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            setMessage('Please fill in all fields correctly.');
            setSeverity('error');
            setOpen(true);
            return;
        }

        const method = editDistributorId ? 'PUT' : 'POST';
        const url = editDistributorId
            ? `https://servermunidasa.ccsoftwares.com/distributors/updateDistributor/${editDistributorId}`
            : 'https://servermunidasa.ccsoftwares.com/distributors/createDistributor';

        try {
            const response = await axios({
                method,
                url,
                data: formData,
            });

            setMessage(editDistributorId ? 'Distributor updated successfully!' : 'Distributor added successfully!');
            setSeverity('success');
            fetchDistributors();
            setOpen(true);

            // Reset form
            setFormData({
                Distri_ID: '',
                full_name: '',
                Email_Address: '',
                Phone_Number: '',
                address: '',
            });

            setEditDistributorId(null);
            setDialogOpen(false);
        } catch (error) {
            console.error('Error during API request:', error.response || error);
            setMessage('Failed to process the distributor. Please try again.');
            setSeverity('error');
            setOpen(true);
        }
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    const fetchDistributors = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://servermunidasa.ccsoftwares.com/distributors/getAllDistributors');
            const data = await response.json();
            setDistributors(data);
        } catch (error) {
            console.error('Error fetching distributors:', error);
            setError('An error occurred while fetching distributors.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditDistributor = (distributor) => {
        setFormData({
            Distri_ID: distributor.Distri_ID,
            full_name: distributor.full_name,
            Email_Address: distributor.Email_Address,
            Phone_Number: distributor.Phone_Number,
            address: distributor.address,
        });
        setErrors({});
        setEditDistributorId(distributor.Distri_ID);
        setDialogOpen(true);
    };



    const handleDeleteDistributor = async (id) => {


        if (window.confirm('Are you sure you want to delete this distributor?')) {
            try {
                await axios.delete(`https://servermunidasa.ccsoftwares.com/distributors/deleteDistributor/${id}`);
                fetchDistributors();
                setMessage('Distributor deleted successfully!');
                setSeverity('success');
                setOpen(true);
            } catch (error) {
                console.error('Error deleting distributor:', error);
                setMessage('Failed to delete distributor. Please try again.');
                setSeverity('error');
                setOpen(true);
            }
        }
    };


    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
    };

    const handleDeleteDistributorClick = (id) => {
        setDeleteDistId(id);
        setDeleteDialogOpen(true);
    };

    const handleDialogOpen = () => {
        setFormData({
            Distri_ID: '',
            full_name: '',
            Email_Address: '',
            Phone_Number: '',
            address: '',
        });
        setErrors({});
        setEditDistributorId(null);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    useEffect(() => {
        fetchDistributors();
    }, []);

    const handleViewImages = (nicFront, nicBack) => {
        setNicFrontImage(nicFront);
        console.log(nicFront)
        setNicBackImage(nicBack);
        setImageDialog(true);
    };

    const removeImage = () => {
        setImageDialog(false);
        setNicFrontImage('');
        setNicBackImage('');
    };


    const navigate = useNavigate();

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: 'black',
            fontWeight: 'bold',
            fontSize: '2rem',
            minHeight: '100vh',
            paddingTop: '20px',
        }}>

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <Typography id="alert-dialog-description">
                        Are you sure you want to delete this distributor?
                    </Typography>
                </DialogContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '10px 70px' }}>
                    <Button variant="outlined" color="error" onClick={handleConfirmDelete}>
                        OK
                    </Button>
                    <Button variant="outlined" onClick={handleCancelDelete}>
                        Cancel
                    </Button>
                </Box>
            </Dialog>


            <Card sx={{ width: '97%', padding: '20px', marginTop: '10px' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button
                            style={{
                                backgroundColor: 'white',
                                color: 'Black',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 30,
                                marginTop: 5,
                            }}
                            onClick={() => navigate(-1)}
                        >
                            <ArrowCircleLeftIcon fontSize='2' />
                        </button>
                        <h2 style={{ margin: 0 }}>DISTRIBUTORS</h2>
                        <Button
                            variant="outlined"
                            sx={{ color: 'black', borderColor: 'black' }}
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={handleDialogOpen}
                        >
                            Add Distributor
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                        <TextField
                            size='small'
                            variant="outlined"
                            placeholder="Search Distributors"
                            sx={{
                                marginTop: '5px',
                                width: '35%',
                                marginBottom: '20px',
                                borderRadius: '15px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '30px',
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon sx={{ marginRight: '8px', color: 'grey' }} />
                                )
                            }}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
                        />
                    </Box>
                    {/* Distributor Table */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                            <svg width={0} height={0}>
                                <defs>
                                    <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor="#e01cd5" />
                                        <stop offset="100%" stopColor="#1CB5E0" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            <CircularProgress sx={{ 'svg circle': { stroke: 'url(#my_gradient)' } }} />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ width: '100%', maxHeight: '550px' }}>
                            <Table stickyHeader sx={{ minWidth: 700 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell><b>ID</b></StyledTableCell>
                                        <StyledTableCell><b>NAME</b></StyledTableCell>
                                        <StyledTableCell><b>EMAIL</b></StyledTableCell>
                                        <StyledTableCell><b>PHONE</b></StyledTableCell>
                                        <StyledTableCell><b>NIC IMAGE</b></StyledTableCell>
                                        <StyledTableCell><b>ACTION</b></StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {distributors.filter(distributor => distributor.full_name.toLowerCase().includes(searchQuery)).map((distributor) => (
                                        <StyledTableRow key={distributor.Distri_ID}>
                                            <StyledTableCell>{distributor.Distri_ID}</StyledTableCell>
                                            <StyledTableCell>{distributor.full_name}</StyledTableCell>
                                            <StyledTableCell>{distributor.Email_Address}</StyledTableCell>
                                            <StyledTableCell>{distributor.Phone_Number}</StyledTableCell>
                                            <StyledTableCell>
                                                <IconButton
                                                    aria-label="view images"
                                                    onClick={() => handleViewImages(distributor.nic_front, distributor.nic_back)}
                                                >
                                                    <CollectionsIcon />
                                                </IconButton>
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <IconButton aria-label="edit" onClick={() => handleEditDistributor(distributor)}>
                                                    <EditIcon />
                                                </IconButton>
                                                {/* <IconButton aria-label="delete" onClick={() => handleDeleteDistributor(distributor.Distri_ID)}>
                                                    <DeleteForeverIcon />
                                                </IconButton> */}

                                                <IconButton aria-label="delete" onClick={() => handleDeleteDistributorClick(distributor.Distri_ID)}>
                                                    <DeleteForeverIcon />
                                                </IconButton>
                                            </StyledTableCell>


                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Dialog for Add/Edit Distributor */}
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <Card sx={{ minWidth: 250, minHeight: 150, borderRadius: 3 }}>
                    <CardContent>
                        <IconButton
                            onClick={handleDialogClose}
                            sx={{
                                color: 'red',
                                position: 'absolute',
                                top: -5,
                                right: -5,
                                borderRadius: '50%',
                            }}
                        >
                            <HighlightOffIcon />
                        </IconButton>
                        <Typography variant="h5" component="div" sx={{ mb: 2, textAlign: 'center' }}>
                            Distributor Details
                        </Typography>
                        <Typography variant="h5" component="div">
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <TextField
                                                label="Distributor ID"
                                                name="Distri_ID"
                                                size='small'
                                                value={formData.Distri_ID}
                                                onChange={handleChange}
                                                error={!!errors.Distri_ID}
                                                helperText={errors.Distri_ID}
                                                sx={{ m: 1, width: '25ch' }}
                                            />
                                            <TextField
                                                label="Name"
                                                name="full_name"
                                                size='small'
                                                value={formData.full_name}
                                                onChange={handleChange}
                                                error={!!errors.full_name}
                                                helperText={errors.full_name}
                                                sx={{ m: 1, width: '25ch' }}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <TextField
                                                label="Email Address"
                                                name="Email_Address"
                                                size='small'
                                                value={formData.Email_Address}
                                                onChange={handleChange}
                                                error={!!errors.Email_Address}
                                                helperText={errors.Email_Address}
                                                sx={{ m: 1, width: '25ch' }}
                                            />
                                            <TextField
                                                label="Phone Number"
                                                name="Phone_Number"
                                                size='small'
                                                value={formData.Phone_Number}
                                                onChange={handleChange}
                                                error={!!errors.Phone_Number}
                                                helperText={errors.Phone_Number}
                                                sx={{ m: 1, width: '25ch' }}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <TextField
                                                label="Address"
                                                name="address"
                                                size='small'
                                                value={formData.address}
                                                onChange={handleChange}
                                                error={!!errors.address}
                                                helperText={errors.address}
                                                multiline
                                                rows={4}
                                                sx={{ m: 1, width: 'calc(50ch + 20px)' }}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <TextField
                                            type="file"
                                            name="nicFront"
                                            size='small'
                                            onChange={handleFileChange}
                                            error={!!errors.nicFront}
                                            helperText={errors.nicFront}
                                            InputLabelProps={{ shrink: true }}
                                            label="NIC Front Image"
                                            style={{ margin: '8px', width: '25ch' }}
                                        />
                                        <TextField
                                            type="file"
                                            name="nicBack"
                                            size='small'
                                            onChange={handleFileChange}
                                            error={!!errors.nicBack}
                                            helperText={errors.nicBack}
                                            InputLabelProps={{ shrink: true }}
                                            label="NIC Back Image"
                                            style={{ margin: '8px', width: '25ch' }}
                                        />
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                                        <Button
                                            variant="outlined"
                                            sx={{ color: '#118AB2', borderColor: '#118AB2', marginRight: '10px' }}
                                            type="submit"
                                            startIcon={<SaveIcon />}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </form>
                            </Box>
                        </Typography>
                    </CardContent>
                </Card>
            </Dialog>

            {/* NIC Image Dialog */}
            <Dialog open={imageDialog} onClose={removeImage}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        paddingTop: "1vh",
                        paddingRight: "1vw",
                    }}
                >
                    <IconButton onClick={removeImage}>
                        <CancelIcon sx={{ color: "red" }} />
                    </IconButton>
                </Box>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <Card
                            style={{
                                height: 400,
                                width: 300,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            {nicFrontImage ? (
                                <img
                                    src={`https://servermunidasa.ccsoftwares.com/Images/${nicFrontImage}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                    }}
                                    alt="NIC Front"
                                />
                            ) : (
                                <p>No NIC Front Image</p>
                            )}
                        </Card>
                        <Card
                            style={{
                                height: 400,
                                width: 300,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            {nicBackImage ? (
                                <img
                                    src={`https://servermunidasa.ccsoftwares.com/Images/${nicBackImage}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                    }}
                                    alt="NIC Back"
                                />
                            ) : (
                                <p>No NIC Back Image</p>
                            )}
                        </Card>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Snackbar for success/error messages */}
            <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
                <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
                    {message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Distributor;
